import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { HStack, Text } from '@squire/ui';

import { DateFormat, formatDate } from '@/utils/dateUtils';

import { CalendarDay } from './CalendarDay';
import { styles } from './styles';

interface CalendarMonthProps {
  month?: Date;
  currentMonth?: Date;
  width: number;
  selectedDate?: Date;
  isDateDisabled: (date: Date) => boolean;
  isDateWaitlistOnly?: (date: Date) => boolean;
  handleDateSelect: (date: Date) => void;
  testID?: string;
  minDate?: Date;
  maxDate?: Date;
}

const CalendarMonth = ({
  month = new Date(),
  currentMonth = new Date(),
  width,
  selectedDate,
  isDateDisabled,
  isDateWaitlistOnly,
  handleDateSelect,
  testID,
  minDate,
  maxDate,
}: CalendarMonthProps) => {
  const { i18n } = useTranslation();

  const viewMonthStart = month
    ? startOfMonth(month)
    : startOfMonth(currentMonth);
  const viewMonthEnd = month ? endOfMonth(month) : endOfMonth(currentMonth);
  const calendarStart = startOfWeek(viewMonthStart);
  const calendarEnd = endOfWeek(viewMonthEnd);

  const monthDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const weekDayLabels = useMemo(() => {
    const start = startOfWeek(new Date());
    return eachDayOfInterval({ start, end: addDays(start, 6) }).map((day) =>
      formatDate(day, DateFormat.WEEKDAY, i18n.language).toUpperCase(),
    );
  }, [i18n.language]);

  const weeks: Date[][] = [];
  for (let i = 0; i < monthDays.length; i += 7) {
    weeks.push(monthDays.slice(i, i + 7));
  }

  const getIsDateOutOfRange = (date: Date) => {
    let isOutOfRange = false;

    if (minDate && !isSameDay(date, minDate) && isBefore(date, minDate)) {
      isOutOfRange = true;
    }

    if (maxDate && !isSameDay(date, maxDate) && isAfter(date, maxDate)) {
      isOutOfRange = true;
    }

    return isOutOfRange;
  };

  return (
    <View style={styles.monthContainer(width)}>
      <HStack style={styles.weekDayLabels}>
        {weekDayLabels.map((label, index) => (
          <Text
            align="center"
            variant="s"
            color="textSecondary"
            key={`${month.valueOf()}-week-day-label-${index}`}
          >
            {label}
          </Text>
        ))}
      </HStack>

      {weeks.map((week, weekIndex) => (
        <HStack
          key={`${month.valueOf()}-week-${weekIndex}`}
          style={styles.weekRow}
        >
          {week.map((day) => (
            <CalendarDay
              date={day}
              key={`day-${formatDate(day, DateFormat.DAY_MONTH_YEAR, i18n.language)}`}
              selected={selectedDate && isSameDay(day, selectedDate)}
              disabled={isDateDisabled(day)}
              isWaitlistOnly={isDateWaitlistOnly?.(day)}
              hidden={!isSameMonth(day, month)}
              onPress={() => handleDateSelect(day)}
              testID={`${testID}-day-${formatDate(day, DateFormat.DAY_MONTH_YEAR, i18n.language)}`}
              isOutOfRange={getIsDateOutOfRange(day)}
            />
          ))}
        </HStack>
      ))}
    </View>
  );
};

CalendarMonth.displayName = 'CalendarMonth';
export { CalendarMonth };
